import React, { useRef, useState, useEffect } from "react";
import { withStyles, InputBase, Box } from "@material-ui/core";
import PropTypes from "prop-types";

const OtpInputBox = withStyles((theme) => ({
  root: {
    width: '50px',
    height: '40px',
    margin: '0 6px',
    '&:first-child': {
      marginLeft: 0,
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  input: {
    width: '100%',
    height: '100%',
    borderRadius: '8px',
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    fontSize: '17px',
    fontWeight: '500',
    textAlign: 'center',
    padding: 0,
    transition: 'all 0.2s ease',
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    '&:focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      outline: 'none',
    },
    '&:hover': {
      borderColor: '#9ca3af',
    },
  },
}))(InputBase);

const OtpInput = (props) => {
  const {numInputs, onChange, value, disabled = false} = props;
  const inputs = useRef([]);
  const toDigitArray = (val) => {
    const chars = (val || '').split('').slice(0, numInputs);
    while (chars.length < numInputs) chars.push('');
    return chars;
  };
  const [otp, setOtp] = useState(() => toDigitArray(value));

  useEffect(() => {
    setOtp(toDigitArray(value));
  }, [value]);

  const handleChange = (e, index) => {
    if (disabled) return;
    // keep only digits and a single character
    const raw = e.target.value || "";
    const value = raw.replaceAll(/\D/g, "").slice(0, 1);
    
    // Update state with actual value
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Notify parent
    if (onChange) {
      onChange(newOtp.join(""));
    }

    // Auto-focus to next input
    if (value.length === 1 && index < numInputs - 1) {
      setTimeout(() => inputs.current[index + 1]?.focus(), 0);
    }
  };

  const handleKeyDown = (e, index) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    // allow navigation and editing keys
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
    if (allowedKeys.includes(e.key)) {
      if (e.key === "Backspace") {
        if (!otp[index] && index > 0) {
          inputs.current[index - 1].focus();
        }
      }
      return;
    }

    // block any non-digit printable characters (this blocks +, e, letters, etc.)
    if (e.key.length === 1 && !/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handlePaste = (e, index) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    const paste = (e.clipboardData || window.clipboardData).getData("Text") || "";
    const digits = paste.replaceAll(/\D/g, "");
    if (!digits) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    const first = digits.charAt(0);
    
    // Update state with actual value
    const newOtp = [...otp];
    newOtp[index] = first;
    setOtp(newOtp);
    
    // Notify parent
    if (onChange) {
      onChange(newOtp.join(""));
    }
    
    if (index < numInputs - 1) {
      inputs.current[index + 1].focus();
    }
  };

  return (
    <Box 
      display="flex" 
      justifyContent="flex-start" 
      alignItems="center"
      flexWrap="nowrap"
      style={{ gap: '0px' }}
    >
      {[...new Array(numInputs)].map((val, index) => (
        <OtpInputBox
          key={val}
          type="tel"
          value={otp[index]}
          disabled={disabled}
          inputRef={(el) => (inputs.current[index] = el)}
          inputProps={{ 
            maxLength: 1, 
            inputMode: "numeric", 
            pattern: "[0-9]*",
            autoComplete: "off",
            readOnly: disabled
          }}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
        />
      ))}
    </Box>
  );
};

export default OtpInput;

OtpInput.propTypes = {
  numInputs: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  value: PropTypes.string,
  disabled: PropTypes.bool,
};